import { baseApi } from "./baseApi";

// Actual template structure from backend
export interface UIBlockReference {
  id: string;
  name: string;
  version: string;
  html: string;
  css: string;
  js: string | null;
}

export interface TemplateBlock {
  id: string;
  templateId: string;
  uiBlockId: string;
  sortOrder: number;
  name: string;
  htmlContent: string;
  cssContent: string;
  jsContent: string | null;
  editableFields: Record<string, string>; // field name -> field label
  defaultValues: Record<string, any>;
  uiBlockVersion: string;
  snapshotHash: string;
  createdAt: string;
  uiBlock: UIBlockReference;
}

export interface TemplateConfigField {
  name: string;
  type: "text" | "textarea" | "color" | "number" | "select" | "boolean";
  label: string;
  defaultValue: any;
  category: string;
  options?: string[]; // For select type
  min?: number; // For number type
  max?: number; // For number type
  required?: boolean;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  businessType: string;
  isPremium: boolean;
  thumbnailImage?: string;
  previewImage?: string;
  templateBlocks?: TemplateBlock[]; // Only available when fetching individual template
  editableFields?: Record<string, string>; // All editable fields across blocks
  defaultValues?: Record<string, any>; // All default values
  customizationOptions?: {
    fonts: Record<string, string>;
    colors: Record<string, string>;
    spacing: Record<string, string>;
  };
  tags: string[];
  complexity: string;
  sortOrder: number;
  usageCount: number;
  isActive: boolean;
  responsive: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    landingPages: number;
  };
  // Legacy support
  thumbnailUrl?: string;
  previewUrl?: string;
  features?: string[];
}

export interface TemplateCategory {
  name: string;
  count: number;
  templates: Template[];
}

// Actual API response structure: templates grouped by category name
export interface TemplatesResponse {
  [categoryName: string]: Template[];
}

// Keep old interface for backward compatibility until migration is complete
export interface LandingPageTemplate {
  name: string;
  description: string;
  features: string[];
  configFields: TemplateConfigField[];
  uiblocks: any;
  thumbnailUrl?: string;
}

export interface LandingPageConfig {
  heroTitle?: string;
  heroSubtitle?: string;
  ctaText?: string;
  template?: string;
  theme?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  features?: Array<{
    title: string;
    description: string;
  }>;
  testimonials?: Array<{
    name: string;
    content: string;
    company?: string;
  }>;
  [key: string]: any;
}

export interface LandingPage {
  id: string;
  title: string;
  slug: string;
  template: string;
  domain: string | null;
  customDomain?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    name: string;
    email: string;
    domain: string;
    customDomain: string | null;
  };
  leadCount: number;
  url: string;
  config?: LandingPageConfig;
  templateInfo?: LandingPageTemplate;
}

export interface LandingPageStats {
  totalPages: number;
  activePages: number;
  inactivePages: number;
  totalLeads: number;
  averageLeadsPerPage: number;
  templateDistribution: Record<string, number>;
}

export interface CreateLandingPageRequest {
  title: string;
  slug: string;
  template: string;
  customerId: string;
  domain?: string;
  config?: LandingPageConfig;
}

export interface UpdateLandingPageRequest {
  title?: string;
  isActive?: boolean;
  config?: Partial<LandingPageConfig>;
}

export interface SavePageRequest {
  title: string;
  slug: string;
  customerId: string;
  blocks: Array<{
    blockId: string;
    configuration: Record<string, any>;
    styleOverrides?: Record<string, any>;
    isVisible?: boolean;
  }>;
}

export interface AIBuildRequest {
  prompt: string;
  title?: string;
  description?: string;
  styles?: {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    fontFamily?: string;
  };
  sections?: string[];
  businessInfo?: {
    name?: string;
    industry?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  // For iterative modifications
  context?: {
    previousHtml?: string;
    modificationRequest?: string;
    variables?: Record<string, any>;
  };
}

export interface AIBuildResponse {
  html: string;
  css?: string;
  variables?: Record<string, any>;
  config?: Record<string, any>;
  metadata?: {
    timestamp?: string;
    businessName?: string;
    title?: string;
    sections?: string[];
  };
}

export interface ExternalDomainRequest {
  customDomain: string;
}

export interface ExternalDomainResponse {
  id: string;
  slug: string;
  customDomain: string;
  customerId: string;
  message: string;
}

export interface LandingPageListParams {
  page?: number;
  limit?: number;
  search?: string;
  template?: string;
  customerId?: string;
  isActive?: boolean;
}

export const pagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // New templates API endpoints
    getTemplates: builder.query<TemplatesResponse, void>({
      query: () => "/templates",
      transformResponse: (response: {
        success: boolean;
        data: TemplatesResponse;
      }) => response.data,
      providesTags: ["Template"],
    }),

    getTemplateById: builder.query<Template, string>({
      query: (id) => `/templates/${id}`,
      transformResponse: (response: { success: boolean; data: Template }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "Template", id }],
    }),

    // Legacy endpoint for backward compatibility
    getLegacyTemplates: builder.query<
      { [key: string]: LandingPageTemplate },
      void
    >({
      query: () => "/pages/templates",
      transformResponse: (response: {
        success: boolean;
        data: { [key: string]: LandingPageTemplate };
      }) => response.data,
    }),

    getPages: builder.query<
      {
        pages: LandingPage[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      LandingPageListParams
    >({
      query: (params) => ({
        url: "/pages",
        params,
      }),
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
      providesTags: ["LandingPage"],
    }),

    getPageById: builder.query<
      LandingPage,
      { id: string; includeConfig?: boolean }
    >({
      query: ({ id, includeConfig = true }) => ({
        url: `/pages/${id}`,
        params: { includeConfig },
      }),
      transformResponse: (response: { success: boolean; data: LandingPage }) =>
        response.data,
      providesTags: (result, error, { id }) => [{ type: "LandingPage", id }],
    }),

    getPageStats: builder.query<
      LandingPageStats,
      { customerId?: string; pageId?: string }
    >({
      query: (params) => ({
        url: "/pages/stats",
        params,
      }),
      transformResponse: (response: {
        success: boolean;
        data: LandingPageStats;
      }) => response.data,
      providesTags: ["LandingPageStats"],
    }),

    createPage: builder.mutation<LandingPage, CreateLandingPageRequest>({
      query: (data) => {
        console.log("createPage API called with data:", data);
        console.log("Data JSON stringified:", JSON.stringify(data));
        return {
          url: "/pages",
          method: "POST",
          body: data,
        };
      },
      transformResponse: (response: { success: boolean; data: LandingPage }) =>
        response.data,
      invalidatesTags: ["LandingPage", "LandingPageStats"],
    }),

    updatePage: builder.mutation<
      LandingPage,
      { id: string; data: UpdateLandingPageRequest }
    >({
      query: ({ id, data }) => ({
        url: `/pages/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: LandingPage }) =>
        response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: "LandingPage", id },
        "LandingPage",
        "LandingPageStats",
      ],
    }),

    deletePage: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/pages/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { success: boolean; message: string }) => ({
        message: response.message,
      }),
      invalidatesTags: ["LandingPage", "LandingPageStats"],
    }),

    getPublicPage: builder.query<LandingPage, string>({
      query: (slug) => `/pages/public/${slug}`,
      transformResponse: (response: { success: boolean; data: LandingPage }) =>
        response.data,
    }),

    getPageHtml: builder.query<
      {
        htmlContent: string;
        metadata: {
          slug: string;
          customerId: string;
          title: string;
          description: string;
          keywords: string;
          createdAt: string;
          updatedAt: string;
        };
        url: string;
      },
      string
    >({
      query: (id) => `/pages/${id}/html`,
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
    }),

    generatePageHtml: builder.mutation<
      {
        url: string;
        htmlContent: string;
      },
      string
    >({
      query: (id) => ({
        url: `/pages/${id}/generate-html`,
        method: "POST",
      }),
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
      invalidatesTags: (result, error, id) => [{ type: "LandingPage", id }],
    }),

    savePage: builder.mutation<LandingPage, SavePageRequest>({
      query: (data) => {
        console.log("savePage API called with data:", data);
        console.log("Data JSON stringified:", JSON.stringify(data));
        return {
          url: "/pages/save",
          method: "POST",
          body: data,
        };
      },
      transformResponse: (response: { success: boolean; data: LandingPage }) =>
        response.data,
      invalidatesTags: ["LandingPage", "LandingPageStats"],
    }),

    buildWithAI: builder.mutation<AIBuildResponse, AIBuildRequest>({
      query: (data) => ({
        url: "/pages/ai/build",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: AIBuildResponse }) =>
        response.data,
    }),

    associateExternalDomain: builder.mutation<
      ExternalDomainResponse,
      { customerId: string; data: ExternalDomainRequest }
    >({
      query: ({ customerId, data }) => ({
        url: `/pages/external-domain/${customerId}`,
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: ExternalDomainResponse }) =>
        response.data,
      invalidatesTags: ["LandingPage", "LandingPageStats"],
    }),

    // Removed renderTemplate - now doing client-side rendering">
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useGetLegacyTemplatesQuery,
  useGetPagesQuery,
  useGetPageByIdQuery,
  useGetPageStatsQuery,
  useCreatePageMutation,
  useUpdatePageMutation,
  useDeletePageMutation,
  useGetPublicPageQuery,
  useGetPageHtmlQuery,
  useGeneratePageHtmlMutation,
  useSavePageMutation,
  useBuildWithAIMutation,
  useAssociateExternalDomainMutation,
} = pagesApi;
